// Display space (docs/format/README.md §5): the page as shown at scale 1 after /Rotate,
// origin top-left, Y down. Matches PDF.js's PageViewport at scale 1.

import type { Anchor, Page, Point, Rect } from "./types.ts";

/** User space → display space for one page. */
export function toDisplay(page: Pick<Page, "rotate" | "view">, x: number, y: number): Point {
  const [vx0, vy0, vx1, vy1] = page.view;
  switch (page.rotate) {
    case 90:
      return [y - vy0, x - vx0];
    case 180:
      return [vx1 - x, y - vy0];
    case 270:
      return [vy1 - y, vx1 - x];
    default:
      return [x - vx0, vy1 - y];
  }
}

/** Display space → user space for one page (inverse of toDisplay). */
export function toUser(page: Pick<Page, "rotate" | "view">, X: number, Y: number): Point {
  const [vx0, vy0, vx1, vy1] = page.view;
  switch (page.rotate) {
    case 90:
      return [Y + vx0, X + vy0];
    case 180:
      return [vx1 - X, Y + vy0];
    case 270:
      return [vx1 - Y, vy1 - X];
    default:
      return [X + vx0, vy1 - Y];
  }
}

/** Display size of a page: [width, height]. */
export function displaySize(page: Pick<Page, "rotate" | "view">): [number, number] {
  const w = page.view[2] - page.view[0];
  const h = page.view[3] - page.view[1];
  return page.rotate === 90 || page.rotate === 270 ? [h, w] : [w, h];
}

/** A user-space rect as a normalized display-space rect [left, top, right, bottom]. */
export function rectToDisplay(page: Pick<Page, "rotate" | "view">, r: Rect): Rect {
  const a = toDisplay(page, r[0], r[1]);
  const b = toDisplay(page, r[2], r[3]);
  return [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])];
}

/** A display-space rect back to a normalized user-space rect. */
export function rectToUser(page: Pick<Page, "rotate" | "view">, r: Rect): Rect {
  const a = toUser(page, r[0], r[1]);
  const b = toUser(page, r[2], r[3]);
  return [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])];
}

/**
 * The anchor's reference point in display space: a pin's point, or the top-left corner of the
 * region's topmost rect (ties: leftmost). Show order and leaders start here.
 */
export function referencePoint(page: Pick<Page, "rotate" | "view">, anchor: Anchor): Point {
  if (anchor.shape === "pin") return toDisplay(page, anchor.point[0], anchor.point[1]);
  let best: Point | null = null;
  for (const r of anchor.rects) {
    const d = rectToDisplay(page, r);
    if (!best || d[1] < best[1] || (d[1] === best[1] && d[0] < best[0])) best = [d[0], d[1]];
  }
  return best ?? [0, 0];
}

/** True when the anchor lies entirely outside the page's visible box. */
export function anchorOutsideView(page: Pick<Page, "view">, anchor: Anchor): boolean {
  const [vx0, vy0, vx1, vy1] = page.view;
  if (anchor.shape === "pin") {
    const [x, y] = anchor.point;
    return x < vx0 || x > vx1 || y < vy0 || y > vy1;
  }
  return anchor.rects.every((r) => r[2] < vx0 || r[0] > vx1 || r[3] < vy0 || r[1] > vy1);
}
